/** @jsx jsx */
import React, { useRef, useMemo } from 'react';
import { jsx } from '@emotion/core';

import { Box } from '@material-ui/core';
import PageContainer from 'components/PageContainer';
import ActionButtonsContainer, {
  TextAction,
} from 'components/ActionButtonsContainer';
import Title from 'components/Title';
import HorizontalRule from 'components/HorizontalRule';

import useTranslate from 'hooks/useTranslate';
import { Trans, useMarkdownTranslate } from 'hooks/useTranslate/useTranslate';

import RestartLink from '../../form/RestartLink';
import styles from '../styles';
import { conformToMask, decimalMoneyMask } from 'utils/masks';
import { InstallmentsOffer, SplitPayOffer } from 'state/financeOffers';
import InstallmentCheckout from './InstallmentCheckout';
import SplitPayCheckout from './SplitPayCheckout';
import CreditCardExistingAccount from '../CreditCardExistingAccount';
import CreditCard from '../CreditCard';
import { FirstOffersEnum } from 'hooks/useQueryParameters';
import { FormikProps } from 'formik';

import { FormValues } from '../UnifiedPrequalOffer';

import { useDebounce } from 'use-debounce';
import CreditCardNotPreapproved from '../CreditCardNotPreapproved';
import { PrequalOffer } from 'state/prequalOffer';
import CreditCardFallback from 'features/unified/Offer/CheckoutOffer/CreditCardFallback';
import { ExpandedPanel } from 'state/unifiedWidgets';

import { OfferWidgetType } from 'hooks/useAnalyticVariables';
import CancelButton from 'features/CancelButton';
import useWidgets from './useWidgets';

interface PropTypes {
  intendedPurchaseAmount: string | null;
  formikProps: FormikProps<FormValues>;
  installmentOffers: InstallmentsOffer[];
  splitPayOffers: SplitPayOffer[];
  prequalOffer: PrequalOffer;
  onPanelChange: (
    event: React.ChangeEvent<{}>,
    panelName: string,
    expanded: boolean,
  ) => void;
  expandedPanel: ExpandedPanel | undefined;
  firstOffers?: FirstOffersEnum;
  defaultCartAmount: number;
  openToBuy?: number;
  nextPath: string;
}

const UnifiedCheckoutOfferView: React.FC<PropTypes> = props => {
  const {
    intendedPurchaseAmount,
    defaultCartAmount,
    onPanelChange,
    expandedPanel,
    installmentOffers,
    splitPayOffers,
    prequalOffer,
    formikProps,
    openToBuy,
    nextPath,
  } = props;

  const t = useTranslate();
  const tMd = useMarkdownTranslate();
  const principal = useMemo(
    () =>
      intendedPurchaseAmount
        ? parseFloat(intendedPurchaseAmount)
        : defaultCartAmount,
    [defaultCartAmount, intendedPurchaseAmount],
  );

  const [principalDebounced] = useDebounce(principal, 200);

  const hasWidgets = useWidgets({
    splitPayOffers,
    installmentOffers,
    principalDebounced,
    prequalOffer,
  });

  const {
    hasSplitpayWidget,
    hasInstallmentsWidget,
    hasPrequalWidget,
    hasDivider,
    hasUnhappyPathDivider,
    hasExistingAccountWidget,
    hasNotQualifiedWidget,
    hasPrequalFallbackWidget,
    hasFallbackMessageWidget,
  } = hasWidgets();

  const widgetContext = useMemo(() => {
    const contextArr = [
      hasExistingAccountWidget && 'existingAccount',
      hasFallbackMessageWidget && 'fallback',
      hasInstallmentsWidget && 'installments',
      hasNotQualifiedWidget && 'notQualified',
      hasPrequalWidget && 'prequal',
      hasPrequalFallbackWidget && 'prequalFallback',
      hasSplitpayWidget && 'splitpay',
    ];
    return contextArr
      .sort()
      .filter(i => i)
      .join('-');
  }, [
    hasExistingAccountWidget,
    hasFallbackMessageWidget,
    hasInstallmentsWidget,
    hasNotQualifiedWidget,
    hasPrequalWidget,
    hasPrequalFallbackWidget,
    hasSplitpayWidget,
  ]);

  const installmentId: OfferWidgetType = 'installments';
  const installments = hasInstallmentsWidget ? (
    <div
      id={installmentId}
      key="installments"
      css={styles.paymentOptionContainer}
    >
      <InstallmentCheckout
        intendedPurchaseAmount={principalDebounced}
        panelName="installments"
        expanded={expandedPanel === 'installments'}
        onChange={onPanelChange}
        installmentOffers={installmentOffers}
      />
    </div>
  ) : null;

  const splitPayId: OfferWidgetType = 'splitpay';
  const splitPay = hasSplitpayWidget ? (
    <div
      id={splitPayId}
      key={splitPayOffers[0].id}
      css={styles.paymentOptionContainer}
    >
      <SplitPayCheckout
        intendedPurchaseAmount={principalDebounced}
        panelName={'splitpay'}
        expanded={expandedPanel === 'splitpay'}
        onChange={onPanelChange}
        splitPayOffer={splitPayOffers[0]}
      />
    </div>
  ) : null;

  const prequalificationId: OfferWidgetType = 'prequalOffer';
  const prequalification = hasPrequalWidget ? (
    <div
      id={prequalificationId}
      key="credit"
      css={styles.paymentOptionContainer}
    >
      <CreditCard
        formikProps={formikProps}
        creditLimit={prequalOffer.creditLimit}
        expanded={expandedPanel === 'prequalification'}
        panelName={'prequalification'}
        handlePanelExpandChange={onPanelChange}
        intendedPurchaseAmount={principalDebounced}
        nextPath={nextPath}
      />
    </div>
  ) : null;

  const divider = hasDivider ? (
    <div key="divider" css={styles.paymentOptionContainer}>
      <HorizontalRule orString={t('unified.orHorizontalRule')} />
    </div>
  ) : null;

  const firstOffers = useRef<ExpandedPanel | undefined>(expandedPanel);

  const widgets = useMemo(() => {
    switch (firstOffers.current) {
      case 'installments':
        return [installments, splitPay, divider, prequalification];

      case 'splitpay':
        return [splitPay, installments, divider, prequalification];

      case 'prequalification':
        return [prequalification, divider, installments, splitPay];

      default:
        return [installments, splitPay, divider, prequalification];
    }
  }, [divider, firstOffers, installments, prequalification, splitPay]);

  return (
    <PageContainer maxWidth="sm">
      <Box mb={2}>
        <Title css={styles.promoText}>{tMd('unified.offer.title')}</Title>
      </Box>

      <div css={styles.subTitleText}>
        <div css={styles.offerSubtitle}>{tMd('unified.offer.subtitle')}</div>
      </div>

      <div css={styles.subTitleText}>
        {t('unified.offer.intendedSpend', { context: widgetContext }) ? (
          <React.Fragment>
            <div css={styles.offerSubtitle}>
              {tMd('unified.offer.intendedSpend')}
            </div>
            <Box mb={2} css={styles.fixedIntendedPurchaseAmount}>
              {conformToMask(principalDebounced.toFixed(2), decimalMoneyMask)}
            </Box>
          </React.Fragment>
        ) : null}
      </div>

      {hasExistingAccountWidget && (
        <React.Fragment>
          <div css={styles.paymentOptionContainer}>
            <CreditCardExistingAccount
              expanded={expandedPanel === 'prequalification'}
              panelName={'prequalification'}
              handlePanelExpandChange={onPanelChange}
              openToBuy={openToBuy}
            />
          </div>
          {hasUnhappyPathDivider && (
            <div key="divider" css={styles.paymentOptionContainer}>
              <HorizontalRule orString={t('unified.orHorizontalRule')} />
            </div>
          )}
        </React.Fragment>
      )}

      {widgets}

      {hasNotQualifiedWidget && (
        <React.Fragment>
          {hasUnhappyPathDivider && (
            <div key="divider" css={styles.paymentOptionContainer}>
              <HorizontalRule orString={t('unified.orHorizontalRule')} />
            </div>
          )}
          <div css={styles.paymentOptionContainer}>
            <CreditCardNotPreapproved
              formikProps={formikProps}
              nextPath={nextPath}
              expanded={expandedPanel === 'prequalification'}
              panelName={'prequalification'}
              handlePanelExpandChange={onPanelChange}
            />
          </div>
        </React.Fragment>
      )}

      {hasPrequalFallbackWidget && (
        <React.Fragment>
          {hasUnhappyPathDivider && (
            <div key="divider" css={styles.paymentOptionContainer}>
              <HorizontalRule orString={t('unified.orHorizontalRule')} />
            </div>
          )}
          <div css={styles.paymentOptionContainer}>
            <CreditCardFallback
              expanded={expandedPanel === 'prequalification'}
              panelName={'prequalification'}
              handlePanelExpandChange={onPanelChange}
            />
          </div>
        </React.Fragment>
      )}

      {hasFallbackMessageWidget && (
        <React.Fragment>
          <div css={styles.subTitleText}>
            {t('unified.checkoutOffer.fallbackMessage')}
          </div>
        </React.Fragment>
      )}

      <ActionButtonsContainer>
        <CancelButton>{t('unified.offer.embeddedContinue')}</CancelButton>

        <TextAction>
          <div css={styles.restartText}>
            <Trans
              i18nKey="unified.offer.restartMessage"
              components={[<RestartLink />]}
            />
          </div>
        </TextAction>
      </ActionButtonsContainer>
    </PageContainer>
  );
};

export default UnifiedCheckoutOfferView;
